<script setup>
import { h, computed, onMounted } from 'vue';
import { provideSidebarContext } from './provider';
import { useAccount } from 'dashboard/composables/useAccount';
import { useKbd } from 'dashboard/composables/utils/useKbd';
import { useMapGetter } from 'dashboard/composables/store';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useStorage } from '@vueuse/core';
import { useSidebarKeyboardShortcuts } from './useSidebarKeyboardShortcuts';
import { vOnClickOutside } from '@vueuse/components';

import Button from 'dashboard/components-next/button/Button.vue';
import SidebarGroup from './SidebarGroup.vue';
import SidebarProfileMenu from './SidebarProfileMenu.vue';
import ChannelLeaf from './ChannelLeaf.vue';
import SidebarAccountSwitcher from './SidebarAccountSwitcher.vue';
import Logo from 'next/icon/Logo.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

const props = defineProps({
  isMobileSidebarOpen: {
    type: Boolean,
    default: false,
  },
});

const getInboxIcon = (inbox) => {
  const channelName = (inbox.name || '').toLowerCase();
  if (channelName.includes('whatsapp')) return 'i-lucide-message-circle';
  if (channelName.includes('facebook')) return 'i-lucide-facebook';
  if (channelName.includes('instagram')) return 'i-lucide-instagram';
  if (channelName.includes('email')) return 'i-lucide-mail';
  if (channelName.includes('web')) return 'i-lucide-globe';
  return 'i-lucide-briefcase';
};

const getInboxUnreadCount = (inboxId) => {
  const store = useStore();
  const conversations = store.state.conversations?.all || [];
  return conversations.filter(function(conv) {
    return conv.inbox_id === inboxId &&
      conv.status === 'open' &&
      !conv.messages?.some(function(msg) {
        return msg.account_id === conv.account_id && !msg.private;
      });
  }).length;
};

const emit = defineEmits([
  'closeKeyShortcutModal',
  'openKeyShortcutModal',
  'showCreateAccountModal',
  'closeMobileSidebar',
]);

const { accountScopedRoute } = useAccount();
const store = useStore();
const searchShortcut = useKbd([`$mod`, 'k']);
const { t } = useI18n();

const toggleShortcutModalFn = show => {
  if (show) {
    emit('openKeyShortcutModal');
  } else {
    emit('closeKeyShortcutModal');
  }
};

useSidebarKeyboardShortcuts(toggleShortcutModalFn);

const expandedItem = useStorage(
  'next-sidebar-expanded-item',
  null,
  sessionStorage
);

const setExpandedItem = name => {
  expandedItem.value = expandedItem.value === name ? null : name;
};
provideSidebarContext({
  expandedItem,
  setExpandedItem,
});

const inboxes = useMapGetter('inboxes/getInboxes');
const labels = useMapGetter('labels/getLabelsOnSidebar');
const teams = useMapGetter('teams/getMyTeams');
const contactCustomViews = useMapGetter('customViews/getContactCustomViews');
const conversationCustomViews = useMapGetter(
  'customViews/getConversationCustomViews'
);

onMounted(() => {
  store.dispatch('labels/get');
  store.dispatch('inboxes/get');
  store.dispatch('notifications/unReadCount');
  store.dispatch('teams/get');
  store.dispatch('attributes/get');
  store.dispatch('customViews/get', 'conversation');
  store.dispatch('customViews/get', 'contact');
  store.dispatch('conversations/get', { status: 'open' });
});

const sortedInboxes = computed(() =>
  inboxes.value.slice().sort((a, b) => a.name.localeCompare(b.name))
);

const closeMobileSidebar = () => {
  if (!props.isMobileSidebarOpen) return;
  emit('closeMobileSidebar');
};

const reportRoutes = () => [
  {
    name: 'Reports Agent',
    label: t('SIDEBAR.REPORTS_AGENT'),
    to: accountScopedRoute('agent_reports_index'),
    activeOn: ['agent_reports_show'],
  },
  {
    name: 'Reports Label',
    label: t('SIDEBAR.REPORTS_LABEL'),
    to: accountScopedRoute('label_reports_index'),
  },
  {
    name: 'Reports Inbox',
    label: t('SIDEBAR.REPORTS_INBOX'),
    to: accountScopedRoute('inbox_reports_index'),
    activeOn: ['inbox_reports_show'],
  },
  {
    name: 'Reports Team',
    label: t('SIDEBAR.REPORTS_TEAM'),
    to: accountScopedRoute('team_reports_index'),
    activeOn: ['team_reports_show'],
  },
  {
    name: 'CSAT Reports',
    label: 'CSAT',
    icon: 'i-lucide-star',
    to: accountScopedRoute('csat_reports'),
    activeOn: ['csat_reports'],
  },
  {
    name: 'SLA Reports',
    label: 'SLA',
    icon: 'i-lucide-clock',
    to: accountScopedRoute('sla_reports'),
    activeOn: ['sla_reports'],
  },
];

const menuItems = computed(() => {
  return [
    {
      name: 'Inbox',
      label: t('SIDEBAR.INBOX'),
      icon: 'i-lucide-inbox',
      to: accountScopedRoute('inbox_view'),
      activeOn: ['inbox_view', 'inbox_view_conversation'],
      getterKeys: {
        badge: 'notifications/getHasUnreadNotifications',
      },
    },
    {
      name: 'Conversation',
      label: t('SIDEBAR.CONVERSATIONS'),
      icon: 'i-lucide-message-circle',
      children: [
        {
          name: 'All',
          label: t('SIDEBAR.ALL_CONVERSATIONS'),
          activeOn: ['inbox_conversation'],
          to: accountScopedRoute('home'),
        },
        {
          name: 'Mentions',
          label: t('SIDEBAR.MENTIONED_CONVERSATIONS'),
          activeOn: ['conversation_through_mentions'],
          to: accountScopedRoute('conversation_mentions'),
        },
        {
          name: 'Unattended',
          activeOn: ['conversation_through_unattended'],
          label: t('SIDEBAR.UNATTENDED_CONVERSATIONS'),
          to: accountScopedRoute('conversation_unattended'),
        },
        {
          name: 'Folders',
          label: t('SIDEBAR.CUSTOM_VIEWS_FOLDER'),
          icon: 'i-lucide-folder',
          activeOn: ['conversations_through_folders'],
          children: conversationCustomViews.value.map(function(view) {
            return {
              name: view.name + '-' + view.id,
              label: view.name,
              to: accountScopedRoute('folder_conversations', { id: view.id }),
            };
          }),
        },
        {
          name: 'Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          activeOn: ['conversations_through_team'],
          children: teams.value.map(function(team) {
            return {
              name: team.name + '-' + team.id,
              label: team.name,
              to: accountScopedRoute('team_conversations', { teamId: team.id }),
            };
          }),
        },
        {
          name: 'Labels',
          label: t('SIDEBAR.LABELS'),
          icon: 'i-lucide-tag',
          activeOn: ['conversations_through_label'],
          children: labels.value.map(function(label) {
            return {
              name: label.title + '-' + label.id,
              label: label.title,
              icon: h('span', {
                class: 'size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm',
                style: { backgroundColor: label.color },
              }),
              to: accountScopedRoute('label_conversations', {
                label: label.title,
              }),
            };
          }),
        },
      ],
    },
    ...sortedInboxes.value.map(function(inbox) {
      return {
        name: inbox.name + '-' + inbox.id,
        label: inbox.name,
        icon: getInboxIcon(inbox),
        to: accountScopedRoute('inbox_dashboard', { inbox_id: inbox.id }),
        badge: getInboxUnreadCount(inbox.id),
        component: function(leafProps) {
          return h(ChannelLeaf, {
            label: leafProps.label,
            active: leafProps.active,
            inbox: inbox,
            badge: getInboxUnreadCount(inbox.id),
          });
        },
      };
    }),
    {
      name: 'Contacts',
      label: t('SIDEBAR.CONTACTS'),
      icon: 'i-lucide-contact',
      children: [
        {
          name: 'All Contacts',
          label: t('SIDEBAR.ALL_CONTACTS'),
          to: accountScopedRoute('contacts_dashboard_index', {}, { page: 1, search: undefined }),
          activeOn: ['contacts_dashboard_index', 'contacts_edit'],
        },
        {
          name: 'Active',
          label: t('SIDEBAR.ACTIVE'),
          to: accountScopedRoute('contacts_dashboard_active'),
          activeOn: ['contacts_dashboard_active'],
        },
        {
          name: 'Segments',
          icon: 'i-lucide-group',
          label: t('SIDEBAR.CUSTOM_VIEWS_SEGMENTS'),
          children: contactCustomViews.value.map(function(view) {
            return {
              name: view.name + '-' + view.id,
              label: view.name,
              to: accountScopedRoute('contacts_dashboard_segments_index', { segmentId: view.id }, { page: 1 }),
              activeOn: ['contacts_dashboard_segments_index', 'contacts_edit_segment'],
            };
          }),
        },
        {
          name: 'Tagged With',
          icon: 'i-lucide-tag',
          label: t('SIDEBAR.TAGGED_WITH'),
          children: labels.value.map(function(label) {
            return {
              name: label.title + '-' + label.id,
              label: label.title,
              icon: h('span', {
                class: 'size-[12px] ring-1 ring-n-alpha-1 dark:ring-white/20 ring-inset rounded-sm',
                style: { backgroundColor: label.color },
              }),
              to: accountScopedRoute('contacts_dashboard_labels_index', { label: label.title }, { page: 1, search: undefined }),
              activeOn: ['contacts_dashboard_labels_index', 'contacts_edit_label'],
            };
          }),
        },
      ],
    },
    {
      name: 'Reports',
      label: t('SIDEBAR.REPORTS'),
      icon: 'i-lucide-chart-spline',
      children: [
        {
          name: 'Report Overview',
          label: t('SIDEBAR.REPORTS_OVERVIEW'),
          to: accountScopedRoute('account_overview_reports'),
        },
        {
          name: 'Report Conversation',
          label: t('SIDEBAR.REPORTS_CONVERSATION'),
          to: accountScopedRoute('conversation_reports'),
        },
        ...reportRoutes(),
      ],
    },
    {
      name: 'Settings',
      label: t('SIDEBAR.SETTINGS'),
      icon: 'i-lucide-bolt',
      children: [
        {
          name: 'Settings Agents',
          label: t('SIDEBAR.AGENTS'),
          icon: 'i-lucide-square-user',
          to: accountScopedRoute('agent_list'),
        },
        {
          name: 'Settings Teams',
          label: t('SIDEBAR.TEAMS'),
          icon: 'i-lucide-users',
          to: accountScopedRoute('settings_teams_list'),
        },
        {
          name: 'Settings Automation',
          label: t('SIDEBAR.AUTOMATION'),
          icon: 'i-lucide-workflow',
          to: accountScopedRoute('automation_list'),
        },
        {
          name: 'Canned Responses',
          label: 'Canned Responses',
          icon: 'i-lucide-message-square-quote',
          to: accountScopedRoute('canned_list'),
          activeOn: ['canned_list', 'canned_new', 'canned_edit'],
        },
      ],
    },
  ];
});
</script>

<template>
  <aside
    v-on-click-outside="[
      closeMobileSidebar,
      { ignore: ['#mobile-sidebar-launcher'] },
    ]"
    class="rtl:border-l ltr:border-r flex flex-col text-sm pb-1 fixed top-0 ltr:left-0 rtl:right-0 h-full z-40 transition-transform duration-300 ease-in-out md:static w-[240px] basis-[240px] md:flex-shrink-0 md:ltr:translate-x-0 md:rtl:-translate-x-0"
    :class="[
      {
        'shadow-2xl md:shadow-none': isMobileSidebarOpen,
        'ltr:-translate-x-full rtl:translate-x-full': !isMobileSidebarOpen,
      },
    ]"
    :style="{
      background: 'linear-gradient(180deg, #DC2626 0%, #EF4444 100%)',
      borderColor: '#ffffff20',
    }"
  >
    <!-- Optimus Logo Section -->
    <section class="grid gap-2 mt-4 mb-4 px-3">
      <div class="flex items-center min-w-0 gap-3">
        <div class="grid flex-shrink-0 size-8 place-content-center bg-white/20 rounded-lg">
          <Logo class="size-5 text-white" />
        </div>
        <div class="flex-shrink-0 w-px h-6 bg-white/30" />
        <SidebarAccountSwitcher
          class="flex-grow min-w-0 text-white"
          @show-create-account-modal="emit('showCreateAccountModal')"
        />
      </div>

      <!-- Search Bar -->
      <div class="flex gap-2 mt-2">
        <RouterLink
          :to="{ name: 'search' }"
          class="flex items-center w-full gap-2 px-3 py-1.5 rounded-lg h-8 transition-all duration-200 bg-white/10 border border-white/30 text-white"
        >
          <span class="flex-shrink-0 i-lucide-search size-4" />
          <span class="flex-grow text-left text-sm opacity-90">
            {{ t('COMBOBOX.SEARCH_PLACEHOLDER') }}
          </span>
        </RouterLink>

        <ComposeConversation align-position="right">
          <template #trigger="{ toggle }">
            <Button
              icon="i-lucide-pen-line"
              color="slate"
              size="sm"
              class="!h-8 !w-8 !bg-white/20 !text-white !border-white/30 hover:!bg-white/30"
              @click="toggle"
            />
          </template>
        </ComposeConversation>
      </div>
    </section>

    <!-- Navigation -->
    <nav class="grid flex-grow gap-2 px-3 pb-5 overflow-y-scroll no-scrollbar">
      <ul class="flex flex-col gap-1 m-0 list-none">
        <SidebarGroup
          v-for="item in menuItems"
          :key="item.name"
          v-bind="item"
        />
      </ul>
    </nav>

    <!-- Profile Section -->
    <section class="p-3 border-t flex-shrink-0" style="borderColor: #ffffff30; background: rgba(0,0,0,0.1)">
      <SidebarProfileMenu
        @open-key-shortcut-modal="emit('openKeyShortcutModal')"
        class="text-white"
      />
    </section>
  </aside>
</template>

<style scoped>
:deep(.badge) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  color: #DC2626;
  font-size: 10px;
  font-weight: bold;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  margin-left: 8px;
  vertical-align: middle;
  box-shadow: 0 1px 2px rgba(0,0,0,0.2);
}

:deep(.nbs-channel-leaf) {
  padding-left: 2rem;
}

.no-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.no-scrollbar::-webkit-scrollbar-track {
  background: rgba(255,255,255,0.1);
}
.no-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.3);
  border-radius: 4px;
}
</style>
